import { VimCoreAuthGuard } from '@skyeng/libs/vimbox-core/user';
import { Page403Component } from '../component/page-403/page-403';
import { Page404Component } from '../component/page-404/page-404';
import { Page503Component } from '../component/page-503/page-503';
import { UserRole, type SkyproRoute, type SkyproRouteData } from '@skyeng/libs/skypro/shared';
import { OnboardingGuard } from './guard/onboarding';
import { singularityCabinetRedirectGuard } from './guard/singularity-cabinet-redirect.guard';
import { courseFinalWorkPath } from 'app/shared/const';
import { ReferralRedirectComponent } from '../../../student-cabinet/shared-referral/component/referral-redirect/referral-redirect';

export const routes: SkyproRoute[] = [
  // Страничка о команде
  {
    path: 'about-product-team',
    data: {
      pageId: 'about-product-team',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../public/+about-team-page/routing/routes'),
  },

  // Утильные старнички для разработки/тестирования
  {
    path: 'utils',
    data: {
      pageId: 'utils',
    },
    canActivate: [VimCoreAuthGuard],
    loadChildren: () => import('../../../public/+utils/routing/routes'),
  },

  {
    path: 'referral-banners-preview',
    data: {
      pageId: 'referral-banners-preview',
      roles: [UserRole.curator, UserRole.admin],
    },
    canActivate: [VimCoreAuthGuard],
    loadComponent: () =>
      import('../../../public/+referral-banners-preview/referral-banners-preview').then(
        module => module.ReferralBannersPreviewComponent,
      ),
  },

  // AI Playground - экспериментальная площадка для работы с AI
  {
    path: 'student-cabinet/ai-playground',
    data: {
      pageId: 'student-cabinet/ai-playground',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+ai-playground/routing/routes'),
  },

  // Урок
  {
    path: 'student-cabinet/lesson',
    data: {
      pageId: 'student-cabinet/lesson',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+lesson/routing/routes'),
  },

  {
    path: '',
    data: {
      pageId: 'root',
    },
    redirectTo: 'student-cabinet/showcase',
    pathMatch: 'full',
  },

  {
    path: 'student-cabinet',
    data: {
      pageId: 'student-cabinet',
    },
    redirectTo: 'student-cabinet/showcase',
    pathMatch: 'full',
  },

  // Страничка профиля
  {
    path: 'student-cabinet/profile',
    data: {
      pageId: 'student-cabinet/profile',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+profile/routing/routes'),
  },

  // если редирект в nginx не сработал
  {
    path: 'student-cabinet/referral-program-info',
    data: {
      pageId: 'student-cabinet/referral-program-info',
    },
    component: ReferralRedirectComponent,
  },
  {
    path: 'student-cabinet/showcase',
    data: {
      pageId: 'student-cabinet/showcase',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard, OnboardingGuard],
    loadChildren: () => import('../../../student-cabinet/+showcase/routing/routes'),
  },

  {
    path: 'student-cabinet/services',
    data: {
      pageId: 'services',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard, OnboardingGuard],
    loadComponent: () =>
      import('../../../student-cabinet/+student-services').then(module => module.StudentServicesPageComponent),
  },

  {
    path: 'student-cabinet/stream/:streamId',
    data: {
      pageId: 'student-cabinet/stream',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard, OnboardingGuard],
    loadChildren: () => import('../../../student-cabinet/+course-stream/routing/routes'),
  },

  {
    path: 'student-cabinet/course/:courseId',
    data: {
      pageId: 'student-cabinet/course',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard, OnboardingGuard],
    loadChildren: () => import('../../../student-cabinet/+course/routing/routes'),
  },

  {
    path: 'student-cabinet/course/:courseId/certificate',
    data: {
      pageId: 'student-cabinet/course/certificate',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+course-certificate/routing/routes'),
  },

  // Faq
  {
    path: 'faq',
    data: {
      pageId: 'faq',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadComponent: () => import('../../../public/+faq/').then(module => module.FaqPageComponent),
  },

  {
    path: 'student-cabinet/stream-lesson/:lessonId',
    data: {
      pageId: 'student-cabinet/stream-lesson',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+stream-lesson/routing/routes'),
  },

  {
    path: 'student-cabinet/stream-live/:liveId',
    data: {
      pageId: 'student-cabinet/stream-live',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+stream-live/routing/routes'),
  },

  {
    path: 'student-cabinet/stream-individual-meet/:individualMeetId',
    data: {
      pageId: 'student-cabinet/stream-individual-meet',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+stream-individual-meet/routing/routes'),
  },

  // Роут обратной совместимости со старыми урлами.
  // Если увидел это сообщение после 1 июня 2024, то можешь смело его удалять
  {
    path: `student-cabinet/stream-module/:streamModuleId/coursework`,
    data: {
      pageId: 'redirect',
    },
    redirectTo: `student-cabinet/stream-module/:streamModuleId/${courseFinalWorkPath}`,
  },

  {
    path: `student-cabinet/stream-module/:streamModuleId/${courseFinalWorkPath}`,
    data: {
      pageId: 'student-cabinet/stream-module/course-final-work',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+stream-course-final-work/routing/routes'),
  },
  {
    path: 'student-cabinet/stream-module/:streamModuleId/attestation',
    data: {
      pageId: 'student-cabinet/stream-module/attestation',
    },
    redirectTo: 'student-cabinet/stream-module/:streamModuleId/first-attestation',
  },
  {
    path: 'student-cabinet/stream-module/:streamModuleId/first-attestation',
    data: {
      pageId: 'student-cabinet/stream-module/first-attestation',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+stream-attestation/routing/routes'),
  },
  {
    path: 'student-cabinet/stream-module/:streamModuleId/second-attestation',
    data: {
      pageId: 'student-cabinet/stream-module/second-attestation',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+stream-attestation/routing/routes'),
  },
  {
    path: 'student-cabinet/course/:courseId/lesson/:lessonId',
    data: {
      pageId: 'student-cabinet/course/lesson',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+course-lesson/routing/routes'),
  },

  {
    path: 'student-cabinet/onboarding',
    data: {
      pageId: 'student-cabinet/onboarding',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+onboarding/routing/routes'),
  },

  {
    path: 'docs',
    data: {
      pageId: 'docs',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+docs/routing/routes'),
  },

  {
    path: 'record-book',
    data: {
      pageId: 'record-book',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+record-book/routing/routes'),
  },

  {
    path: 'communications',
    data: {
      pageId: 'student-communications',
    },
    canActivate: [VimCoreAuthGuard, singularityCabinetRedirectGuard],
    loadChildren: () => import('../../../student-cabinet/+communications/routing/routes'),
  },

  // /legal-redirect redirect to https://singularity.academy/license for Laboratory
  {
    path: 'student-cabinet/legal-redirect',
    data: {
      pageId: 'legal-redirect',
    },
    loadChildren: () => new Promise(() => window.location.assign('https://singularity.academy/license')),
  },

  // regular layout pages
  {
    path: '',
    data: { pageId: 'regularLayoutPages' } as SkyproRouteData,
    children: [
      // restricted
      { path: '403', component: Page403Component, data: { pageId: 'page403' } as SkyproRouteData },
      // not found
      { path: '404', component: Page404Component, data: { pageId: 'page404' } as SkyproRouteData },
      // platform error
      { path: '503', component: Page503Component, data: { pageId: 'page503' } as SkyproRouteData },
      // all missed pages
      { path: '**', data: { pageId: 'page404' } as SkyproRouteData, component: Page404Component },
    ],
  },
];
